import { z } from "zod";
import type { AccountId } from "./ids.ts";

/**
 * Operator/staff actions recorded in the audit trail (#152). One value per
 * sensitive cross-account action taken through the admin console. Keep in sync
 * with the DB check constraint on `operator_audit_log.action`.
 */
export const OperatorAuditAction = z.enum([
  "wallet.freeze",
  "wallet.unfreeze",
  "transaction.reverse",
  // Operator agent-token revocation (#207): one token by jti, or all for an account.
  "agent-token.revoke",
  "agent-token.revoke-all",
]);
export type OperatorAuditAction = z.output<typeof OperatorAuditAction>;

/** What an audit row points at — used to interpret `targetId`. */
export const OperatorAuditTargetType = z.enum(["wallet", "transaction", "agent-token", "account"]);
export type OperatorAuditTargetType = z.output<typeof OperatorAuditTargetType>;

/**
 * One recorded operator action. `actor*` is the staff member who acted;
 * `accountId` is the AFFECTED account (the freeze/reversal target's owner), kept
 * denormalized so the trail can be filtered by target account. `actorEmail` is
 * stored as a snapshot so the record stays attributable even if the staff
 * account later changes.
 */
export interface OperatorAuditEntry {
  id: string;
  /**
   * The staff member's ggcoin account, when they have one. `null` for operators
   * authenticated purely via SSO (#227) — they need no ggcoin account; the
   * audit actor is {@link actorEmail}, always present.
   */
  actorAccountId: AccountId | null;
  actorEmail: string;
  action: OperatorAuditAction;
  targetType: OperatorAuditTargetType;
  targetId: string;
  accountId: AccountId;
  reason: string | null;
  createdAt: Date;
}
